import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    Input,
    FilterBar,
    Text,
} from "@athena/ui";
import {
    DeleteOrganizationDocument,
    UpdateOrganizationDocument,
    type OrganizationByIdQuery,
} from "../../graphql/organizations.generated";
import { formatDateTime } from "../../../../global/formatHelpers";
import { SubOrgFormDialog } from "@/components/SubOrgDialogForm";
import { AddOrgMemberDialog } from "./AddOrgMemberDialog";
import { OrgTable, orgColumns } from "@/components/tables/OrgTable";
import { PencilIcon, SquareStack, Trash2, UserRoundPlus } from "lucide-react";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

type SubOrg = NonNullable<OrganizationByIdQuery["organization"]>["subOrganizations"][number];

interface OrganizationSubOrgProps {
    subOrganizations: SubOrg[];
    /**
     * The parent org. Both the owner of any sub-org created here and the eligibility bound for
     * adding people to one: a sub-org's members must already belong to its parent.
     */
    parentOrganizationId: string;
    onSubOrgCreated: () => void;
    onSubOrgMembersAdded: () => void;
    onSubOrgDeleted: () => void;
    onSubOrgRenamed: () => void;
    /** Controls the new-sub-org dialog — opened from a page-header button. */
    newSubOrgOpen: boolean;
    onNewSubOrgOpenChange: (open: boolean) => void;
}

export function OrganizationSubOrg({
    subOrganizations,
    parentOrganizationId,
    onSubOrgCreated,
    onSubOrgMembersAdded,
    onSubOrgDeleted,
    onSubOrgRenamed,
    newSubOrgOpen,
    onNewSubOrgOpenChange,
}: OrganizationSubOrgProps) {
    const navigate = useNavigate();

    const [
        search,
        setSearch,
    ] = useState<string>("");
    // The parent's sub-orgs arrive whole with the organization, so this filters what is already here.
    const term = search.trim().toLowerCase();
    const visibleSubOrgs = subOrganizations.filter(
        (subOrg) => !term || `${subOrg.name} ${subOrg.contactEmailAddress ?? ""}`.toLowerCase().includes(term),
    );

    /** The query's shape mapped onto the shared column shape. */
    const subOrgRows = visibleSubOrgs.map((subOrg) => ({
        ...subOrg,
        memberCount: subOrg.userCount,
        // When it joined, rather than its slug — this list is about the parent's roster over time.
        subtitle: `added ${formatDateTime(subOrg.createdAt)}`,
    }));

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(subOrgRows);

    // Id of the sub-org whose "Add Users" dialog is open — null when closed.
    const [
        addMembersSubOrgId,
        setAddMembersSubOrgId,
    ] = useState<string | null>(null);
    const addMembersSubOrg = subOrganizations.find((s) => s.id === addMembersSubOrgId);

    // The sub-org being edited — null when the dialog is closed.
    const [
        editTarget,
        setEditTarget,
    ] = useState<SubOrg | null>(null);
    const [
        editForm,
        setEditForm,
    ] = useState({ name: "", contactEmailAddress: "", contactPhoneNumber: "" });
    const [
        updateOrganization,
        { loading: savingEdit },
    ] = useMutation(UpdateOrganizationDocument);

    const openEdit = (subOrg: SubOrg) => {
        setEditTarget(subOrg);
        setEditForm({
            name: subOrg.name,
            contactEmailAddress: subOrg.contactEmailAddress ?? "",
            contactPhoneNumber: subOrg.contactPhoneNumber ?? "",
        });
    };

    const handleSaveEdit = async () => {
        if (!editTarget || !editForm.name.trim()) return;
        try {
            await updateOrganization({
                variables: {
                    id: editTarget.id,
                    input: {
                        name: editForm.name.trim(),
                        contactEmailAddress: editForm.contactEmailAddress.trim() || undefined,
                        contactPhoneNumber: editForm.contactPhoneNumber.trim() || undefined,
                    },
                },
            });
            setEditTarget(null);
            onSubOrgRenamed();
        } catch {
            toast.error(`Couldn't save ${editTarget.name}. Please try again.`);
        }
    };

    // The sub-org pending delete confirmation — null when the dialog is closed.
    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState<SubOrg | null>(null);
    const [
        deleteOrganization,
        { loading: deleting },
    ] = useMutation(DeleteOrganizationDocument);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteOrganization({
                variables: { id: deleteTarget.id },
                update(cache) {
                    cache.evict({ id: cache.identify({ __typename: "Organization", id: deleteTarget.id }) });
                    cache.gc();
                },
            });
            setDeleteTarget(null);
            onSubOrgDeleted();
        } catch {
            // A sub-org that still has its own sub-organizations can't be deleted — the
            // FK reference from those children blocks it. Not distinguished from other
            // failures here since the message is accurate for either case.
            toast.error(
                `Couldn't delete ${deleteTarget.name}. If it has its own sub-organizations, remove those first.`,
            );
        }
    };

    return (
        <>
            {subOrganizations.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-32">
                    <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-xl">
                        <SquareStack className="text-muted-foreground h-6 w-6" />
                    </div>
                    <Text weight="medium">No sub-organizations yet</Text>
                    <Text muted className="text-sm">
                        Add a new sub-organization.
                    </Text>
                </div>
            ) : (
                <>
                    {/* Only worth a search box once the list is long enough to hunt through. Four is
                        the point where scanning stops being faster than typing. */}
                    {subOrganizations.length > 4 && (
                        <FilterBar
                            className="mb-3"
                            search={search}
                            onSearchChange={setSearch}
                            searchLabel="Search sub-organizations"
                            searchPlaceholder="Search by name or contact email"
                            searchClassName="max-w-xs"
                            active={term !== ""}
                            onClear={() => setSearch("")}
                            count={{ shown: visibleSubOrgs.length, total: subOrganizations.length }}
                        />
                    )}
                    <ListViewControls view={view} noun="sub-organizations" />

                    <OrgTable
                        columns={[
                            // Headers overridden to this tab's own wording — the shared column
                            // defaults say "Organization", which reads oddly in a list that is
                            // already inside one.
                            { ...orgColumns.identity(), header: "Name" },
                            { ...orgColumns.contactEmail, header: "Contact Email" },
                            orgColumns.members,
                        ]}
                        rows={view.rows}
                        rowKey={(subOrg) => subOrg.id}
                        onRowClick={(subOrg) => navigate(`/organizations/${subOrg.id}`)}
                        filtered={term !== ""}
                        noMatches="No sub-organizations match this search."
                        actions={(row) => {
                            const subOrg = visibleSubOrgs.find((s) => s.id === row.id);
                            if (!subOrg) return null;
                            return (
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="icon-sm"
                                        className="text-muted-foreground hover:text-foreground"
                                        aria-label="Add Users"
                                        onClick={() => setAddMembersSubOrgId(subOrg.id)}
                                    >
                                        <UserRoundPlus className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon-sm"
                                        className="text-muted-foreground hover:text-foreground"
                                        aria-label={`Edit ${subOrg.name}`}
                                        onClick={() => openEdit(subOrg)}
                                    >
                                        <PencilIcon className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon-sm"
                                        className="text-muted-foreground hover:text-destructive"
                                        aria-label={`Delete ${subOrg.name}`}
                                        onClick={() => setDeleteTarget(subOrg)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            );
                        }}
                    />
                </>
            )}
            <SubOrgFormDialog
                open={newSubOrgOpen}
                onOpenChange={onNewSubOrgOpenChange}
                parentOrganizationId={parentOrganizationId}
                onSaved={onSubOrgCreated}
            />
            {addMembersSubOrg && (
                <AddOrgMemberDialog
                    open
                    onOpenChange={(open) => {
                        if (!open) setAddMembersSubOrgId(null);
                    }}
                    organizationId={addMembersSubOrg.id}
                    restrictToOrganizationId={parentOrganizationId}
                    onAdded={onSubOrgMembersAdded}
                />
            )}
            <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Edit {editTarget?.name}</DialogTitle>
                    </DialogHeader>
                    <Field>
                        <FieldLabel htmlFor="sub-org-name">Organization Name</FieldLabel>
                        <Input
                            id="sub-org-name"
                            value={editForm.name}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                            type="text"
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="sub-org-contact-email">Contact Email Address</FieldLabel>
                        <Input
                            id="sub-org-contact-email"
                            type="email"
                            value={editForm.contactEmailAddress}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, contactEmailAddress: e.target.value }))}
                            placeholder="email@example.com"
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="sub-org-contact-phone">Contact Phone Number</FieldLabel>
                        <Input
                            id="sub-org-contact-phone"
                            type="text"
                            value={editForm.contactPhoneNumber}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, contactPhoneNumber: e.target.value }))}
                            placeholder="(555) 555-5555"
                        />
                    </Field>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)}>
                            Cancel
                        </Button>
                        <Button disabled={!editForm.name.trim() || savingEdit} onClick={() => void handleSaveEdit()}>
                            {savingEdit ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Delete {deleteTarget?.name}</DialogTitle>
                        <DialogDescription>
                            Removes {deleteTarget?.memberCount ?? 0} member
                            {deleteTarget?.memberCount === 1 ? "" : "s"} from this sub-organization. This cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" disabled={deleting} onClick={() => void handleDelete()}>
                            {deleting ? "Deleting…" : `Delete ${deleteTarget?.name ?? ""}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
